import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  products: [],
  services: [],
  rawMaterials: [],
  tempProducts: [],
  tempServices: [],
  tempRawMaterials: [],
  isLoading: false,
  productsPagination: {
    total: 0,
    page: 1,
    pages: 1,
  },
  servicesPagination: {
    total: 0,
    page: 1,
    pages: 1,
  },
  rawMaterialsPagination: {
    total: 0,
    page: 1,
    pages: 1,
  },
  // Add variant-related state
  variantSelection: {
    availableVariants: [],
    selectedVariant: null,
    selectedQuantity: 1,
    loading: false,
    error: null
  }
};

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    setInventory(state, action) {
      const { products, services, rawMaterials } = action.payload;
      state.products = products;
      state.services = services;
      state.rawMaterials = rawMaterials;
      state.isLoading = false;
    },
    setTempRawMaterials(state, action) {
      state.tempRawMaterials = action.payload;
    },
    setRawMaterialsPagination(state, action) {
      state.rawMaterialsPagination = action.payload;
    },
    addRawMaterial(state, action) {
      state.rawMaterials.push(action.payload);
      state.tempRawMaterials.push(action.payload);
    },
    updateRawMaterialData(state, action) {
      const { index, rawMaterial } = action.payload;

      // Defensive check
      if (state.rawMaterials?.data && Array.isArray(state.rawMaterials.data)) {
        state.rawMaterials.data.splice(index, 1, rawMaterial);
      } else if (Array.isArray(state.rawMaterials)) {
        state.rawMaterials.splice(index, 1, rawMaterial);
      }

      if (Array.isArray(state.tempRawMaterials)) {
        state.tempRawMaterials.splice(index, 1, rawMaterial);
      }
    },

    deleteRawMaterialItem(state, action) {
      const { index } = action.payload;
      state.rawMaterials.splice(index, 1);
      state.tempRawMaterials.splice(index, 1);
    },

    setTempProducts(state, action) {
      state.tempProducts = action.payload;
    },
    setProductsPagination(state, action) {
      state.productsPagination = action.payload;
    },
    setServicePagination(state, action) {
      state.servicesPagination = action.payload;
    },
    setTempServices(state, action) {
      state.tempServices = action.payload;
    },
    addProduct(state, action) {
      state.products.push(action.payload);
      state.tempProducts.push(action.payload);
    },
       addService(state, action) {
      if (state.services?.data && Array.isArray(state.services.data)) {
        state.services.data.push(action.payload);
      } else if (Array.isArray(state.services)) {
        state.services.push(action.payload);
      } else {
        state.services = [action.payload];
      }
      state.tempServices.push(action.payload);
    },
    updateProductData(state, action) {
      const { index, product } = action.payload;
      state.products?.splice(index, 1, product);
      state.tempProducts?.splice(index, 1, product);
    },
       updateServiceData(state, action) {
      const { index, service } = action.payload;
      if (state.services?.data && Array.isArray(state.services.data)) {
        state.services.data.splice(index, 1, service);
      } else if (Array.isArray(state.services)) {
        state.services.splice(index, 1, service);
      }
      state.tempServices?.splice(index, 1, service);
    },
    deleteProductItem(state, action) {
      const { index } = action.payload;
      state.products.data.splice(index, 1);
      state.tempProducts.splice(index, 1);
    },
      deleteServiceItem(state, action) {
      const { index } = action.payload;
      if (state.services?.data && Array.isArray(state.services.data)) {
        state.services.data.splice(index, 1);
      } else if (Array.isArray(state.services)) {
        state.services.splice(index, 1);
      }
      state.tempServices.splice(index, 1);
    },
    
    setInventoryIsLoading(state) {
      state.isLoading = true;
    },
    setInventoryIsLoadingFalse(state) {
      state.isLoading = false;
    },

    // New reducers for variant management
    updateVariant(state, action) {
      const { productId, variantId, variantIndex, productIndex, variantData } = action.payload;
      
      // Handle both array and object structures
      if (state.products?.data && Array.isArray(state.products.data)) {
        // If products is in data property
        if (state.products.data[productIndex]) {
          const product = state.products.data[productIndex];
          if (product.variants && Array.isArray(product.variants)) {
            if (product.variants[variantIndex]) {
              state.products.data[productIndex].variants[variantIndex] = {
                ...product.variants[variantIndex],
                ...variantData
              };
            }
          }
        }
      } else if (Array.isArray(state.products)) {
        // If products is direct array
        if (state.products[productIndex]) {
          const product = state.products[productIndex];
          if (product.variants && Array.isArray(product.variants)) {
            if (product.variants[variantIndex]) {
              state.products[productIndex].variants[variantIndex] = {
                ...product.variants[variantIndex],
                ...variantData
              };
            }
          }
        }
      }

      // Also update in tempProducts
      if (Array.isArray(state.tempProducts)) {
        const tempProductIndex = state.tempProducts.findIndex(p => 
          p.productId === productId || p._id === productId
        );
        if (tempProductIndex !== -1) {
          const tempProduct = state.tempProducts[tempProductIndex];
          if (tempProduct.variants && Array.isArray(tempProduct.variants)) {
            const tempVariantIndex = tempProduct.variants.findIndex(v => 
              v.variantId === variantId || v._id === variantId
            );
            if (tempVariantIndex !== -1) {
              state.tempProducts[tempProductIndex].variants[tempVariantIndex] = {
                ...tempProduct.variants[tempVariantIndex],
                ...variantData
              };
            }
          }
        }
      }
    },

    deleteVariant(state, action) {
      const { productId, variantId, variantIndex, productIndex } = action.payload;
      
      // Handle both array and object structures
      if (state.products?.data && Array.isArray(state.products.data)) {
        // If products is in data property
        if (state.products.data[productIndex]) {
          const product = state.products.data[productIndex];
          if (product.variants && Array.isArray(product.variants)) {
            state.products.data[productIndex].variants = 
              product.variants.filter((_, idx) => idx !== variantIndex);
          }
        }
      } else if (Array.isArray(state.products)) {
        // If products is direct array
        if (state.products[productIndex]) {
          const product = state.products[productIndex];
          if (product.variants && Array.isArray(product.variants)) {
            state.products[productIndex].variants = 
              product.variants.filter((_, idx) => idx !== variantIndex);
          }
        }
      }

      // Also update in tempProducts
      if (Array.isArray(state.tempProducts)) {
        const tempProductIndex = state.tempProducts.findIndex(p => 
          p.productId === productId || p._id === productId
        );
        if (tempProductIndex !== -1) {
          const tempProduct = state.tempProducts[tempProductIndex];
          if (tempProduct.variants && Array.isArray(tempProduct.variants)) {
            state.tempProducts[tempProductIndex].variants = 
              tempProduct.variants.filter(v => 
                (v.variantId !== variantId && v._id !== variantId)
              );
          }
        }
      }
    },

    setProductVariants(state, action) {
      const { productId, productIndex, variants } = action.payload;
      
      // Handle both array and object structures
      if (state.products?.data && Array.isArray(state.products.data)) {
        // If products is in data property
        if (state.products.data[productIndex]) {
          state.products.data[productIndex].variants = variants;
          state.products.data[productIndex].hasVariants = variants.length > 0;
        }
      } else if (Array.isArray(state.products)) {
        // If products is direct array
        if (state.products[productIndex]) {
          state.products[productIndex].variants = variants;
          state.products[productIndex].hasVariants = variants.length > 0;
        }
      }

      // Also update in tempProducts
      if (Array.isArray(state.tempProducts)) {
        const tempProductIndex = state.tempProducts.findIndex(p => 
          p.productId === productId || p._id === productId
        );
        if (tempProductIndex !== -1) {
          state.tempProducts[tempProductIndex].variants = variants;
          state.tempProducts[tempProductIndex].hasVariants = variants.length > 0;
        }
      }
    },

    // Variant Selection Reducers
    setVariantSelectionLoading(state, action) {
      state.variantSelection.loading = action.payload;
    },

    setAvailableVariants(state, action) {
      state.variantSelection.availableVariants = action.payload;
    },

    setSelectedVariant(state, action) {
      state.variantSelection.selectedVariant = action.payload;
    },

    setSelectedQuantity(state, action) {
      state.variantSelection.selectedQuantity = action.payload;
    },

    setVariantSelectionError(state, action) {
      state.variantSelection.error = action.payload;
    },

    resetVariantSelection(state) {
      state.variantSelection = {
        availableVariants: [],
        selectedVariant: null,
        selectedQuantity: 1,
        loading: false,
        error: null
      };
    },

    updateVariantQuantity(state, action) {
      const { variantId, quantity } = action.payload;
      const variant = state.variantSelection.availableVariants.find(
        v => v.variantId === variantId
      );
      if (variant) {
        variant.quantity = quantity;
      }
    }
  },
});

export const {
  setInventory,
  setTempProducts,
  setTempServices,
  addProduct,
  addService,
  updateProductData,
  updateServiceData,
  deleteProductItem,
  deleteServiceItem,
  setInventoryIsLoading,
  setInventoryIsLoadingFalse,
  setProductsPagination,
  setServicePagination,
  setTempRawMaterials,
  setRawMaterialsPagination,
  addRawMaterial,
  updateRawMaterialData,
  deleteRawMaterialItem,
  updateVariant,
  deleteVariant,
  setProductVariants,
  // Variant Selection Actions
  setVariantSelectionLoading,
  setAvailableVariants,
  setSelectedVariant,
  setSelectedQuantity,
  setVariantSelectionError,
  resetVariantSelection,
  updateVariantQuantity,
} = inventorySlice.actions;

export default inventorySlice.reducer;